/**
 * ランディングセクションをテンプレ化するダイアログ。
 * landings/{landingId}/sections/{sectionId} の content + style を読み込み、
 * `landingSectionTemplates/{id}` に保存する。
 */
import type { LandingSectionRef } from '@goenhub/shared';
import { doc, getDoc } from 'firebase/firestore';
import { Bookmark, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label, Textarea } from '@/components/ui/input';
import { toast } from '@/components/ui/toast';
import { useSaveLandingSectionTemplate } from '@/hooks/useLandingSectionTemplates';
import { db } from '@/lib/firebase';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  landingId: string;
  /** 対象セクションの ref (type, variant 含む)。null で閉じる */
  section: LandingSectionRef | null;
  sectionLabel?: string;
}

export function LandingSaveTemplateDialog({
  open,
  onOpenChange,
  landingId,
  section,
  sectionLabel,
}: Props) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const save = useSaveLandingSectionTemplate();
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    if (!open) {
      setName('');
      setDescription('');
    }
  }, [open]);

  const handleSave = async () => {
    if (!section) return;
    if (!name.trim()) {
      toast.error('テンプレ名を入力してください');
      return;
    }
    try {
      setFetching(true);
      const snap = await getDoc(
        doc(db, 'landings', landingId, 'sections', section.id),
      );
      if (!snap.exists()) {
        throw new Error('セクション内容が見つかりません');
      }
      const data = snap.data() as {
        content?: Record<string, unknown>;
        style?: Record<string, unknown>;
      };
      await save.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
        type: section.type,
        variant: section.variant,
        content: data.content ?? {},
        style: data.style ?? null,
      });
      toast.success('テンプレートとして保存しました');
      onOpenChange(false);
    } catch (e) {
      toast.error(`保存失敗: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setFetching(false);
    }
  };

  const busy = save.isPending || fetching;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && busy) return;
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bookmark className="size-4 text-[var(--accent)]" />
            セクションをテンプレ化
          </DialogTitle>
          <DialogDescription>
            {sectionLabel
              ? `「${sectionLabel}」の内容を再利用可能なテンプレートとして保存します。`
              : 'このセクションの内容を再利用可能なテンプレートとして保存します。'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-xs">
          <div className="space-y-1">
            <Label htmlFor="ltpl-name">テンプレ名 (必須)</Label>
            <Input
              id="ltpl-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="例: 業界別 Hero - SaaS 向け"
              autoFocus
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="ltpl-desc">説明 (任意)</Label>
            <Textarea
              id="ltpl-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="例: B2B SaaS ランディングの開幕 Hero。CTA 2 個構成"
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            キャンセル
          </Button>
          <Button onClick={handleSave} disabled={busy || !name.trim()}>
            {busy ? (
              <>
                <Loader2 className="size-3.5 mr-1 animate-spin" />
                保存中...
              </>
            ) : (
              <>
                <Bookmark className="size-3.5 mr-1" />
                テンプレを保存
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
